/**
 * BuilderEnvironment — środowisko, w którym kreator formatek renderuje klocki layoutu.
 *
 * Klocek, którego podgląd w kreatorze czyta dane TENANTA (np. „Tabela dodatkowa" pokazuje
 * kolumny wybranej tabeli), musi wiedzieć, czy te dane są w ogóle osiągalne: kreator
 * osadzony w panelu ITE (szablony kartotek) pracuje na koncie administratora platformy —
 * endpointy tenanta (`/settings/additional-tables`…) odpowiadają tam 401, więc zamiast
 * podglądu byłyby tylko zbędne zapytania i komunikaty o błędzie (interceptor panelu ITE wylogowuje
 * wyłącznie po 401 z endpointów `/admin/*` i `/auth/admin/*`, więc sesja administratora nie
 * ucierpi). Funkcje `render` klocków są wołane jak zwykłe funkcje (bez hooków), więc tę wiedzę
 * dostają przez kontekst, który czyta komponent-dziecko.
 *
 * Płótno klocka nie dostaje od kreatora slugu modułu (tylko panel właściwości go dostaje), a
 * klocek „Przycisk SQL" musi znać kartotekę, żeby sprawdzić, czy wybrana procedura jeszcze
 * istnieje — stąd opcjonalny `moduleSlug`.
 *
 * Provider montuje tylko `SystemModuleBuilderPage`. Pozostali hosty wołający `render`
 * klocków (projektant karty kanban, kreator modułów własnych, karty w runtime) providera
 * nie mają — dostają domyślne `tenantData: false`, czyli bezpieczny podgląd bez zapytań.
 */

import { createContext, type ReactNode, useContext, useMemo } from 'react';

export interface BuilderEnvironment {
  /** `true` = kreator działa w zakresie tenanta i może czytać jego dane (tabele dodatkowe…). */
  tenantData: boolean;
  /** Slug modułu systemowego, którego układ edytuje kreator (np. `contractors`); brak = nieznany. */
  moduleSlug?: string;
}

/** Brak providera = host bez dostępu do danych tenanta. */
const DEFAULT_ENVIRONMENT: BuilderEnvironment = { tenantData: false };

const BuilderEnvironmentContext = createContext<BuilderEnvironment>(DEFAULT_ENVIRONMENT);

export function BuilderEnvironmentProvider({
  tenantData,
  moduleSlug,
  children,
}: BuilderEnvironment & { children: ReactNode }) {
  const value = useMemo<BuilderEnvironment>(
    () => ({ tenantData, moduleSlug }),
    [tenantData, moduleSlug],
  );
  return (
    <BuilderEnvironmentContext.Provider value={value}>
      {children}
    </BuilderEnvironmentContext.Provider>
  );
}

export function useBuilderEnvironment(): BuilderEnvironment {
  return useContext(BuilderEnvironmentContext);
}
